import React, { useCallback, useMemo } from 'react';
import { matchPath, useHistory } from 'react-router-dom';

import type { LocationDescriptor, LocationDescriptorObject } from 'history';
import type { INavItem, INavItemProps } from '@/types/navigation';

import { UserType } from '@/store/enums';
import { DropdownItem, NavigationItem } from '@/components/layout/navigation';
import { getNavItems } from '@/helpers/navigation';
import { useAppSelector } from '@/hooks';
import { IFeatureFlag } from '@/store/slices/featureFlags/types';

interface IProps {
  menuType: UserType;
  hasActiveGigs: boolean;
  onCollapseNavItem: () => void;
  featureFlags: IFeatureFlag;
}

const isDropDownMenuItem = (item: INavItem): boolean => item.to.toString().substring(1).startsWith('mnu');
const parseLocationDescriptorToString = (descriptor: LocationDescriptor) =>
  (descriptor as LocationDescriptorObject)?.pathname ?? (descriptor as string);

const NavigationItems = ({ menuType, hasActiveGigs, onCollapseNavItem, featureFlags }: IProps): React.ReactElement => {
  const userPermissions = useAppSelector(state => state.auth.user?.userPermissions);

  const items = useMemo<INavItem[]>(
    () => getNavItems(menuType, hasActiveGigs, featureFlags),
    [hasActiveGigs, menuType, userPermissions, featureFlags],
  );
  const history = useHistory();

  const match = useCallback(
    (path: string): boolean =>
      !!matchPath(path, {
        path: history.location.pathname,
        exact: true,
      }),
    [history.location.pathname],
  );

  return (
    <>
      {items.map<React.ReactElement>((item, index) => {
        const isActive = match(parseLocationDescriptorToString(item.to));
        const itemProps: INavItemProps = {
          isActive,
          item,
          menuType,
          onCollapseNavItem,
        };
        if (isDropDownMenuItem(item)) {
          return <DropdownItem key={index} {...itemProps} />;
        }
        return <NavigationItem key={index} {...itemProps} />;
      })}
    </>
  );
};
export { NavigationItems };
